import { useState, useEffect, useRef, useCallback } from "react";
import { FormikProps } from "formik";
import { denMarkArticleGetApi } from "@/database";
import { toast } from "@/components";
import type { MediaDetailsForm } from "../../../routes/pages/MediaDetail/MediaDetailsForm";

interface ArticlePreviewModalState {
  isOpen: boolean;
  article: any | null;
  articleDetails: any | null;
  loading: boolean;
  error: string | null;
  openedFromInlineSearch: boolean;
  inlineSearchQuery: string;
}

interface UseArticleSearchProps {
  formik: FormikProps<MediaDetailsForm>;
  selectedArticleHost: string;
  createDenmarkEmbedSocialItem: (article: any, type: "lead" | "embed") => any;
  currentMediaDetailsForEdit: any;
  localSocials: any[];
  setLocalSocials: React.Dispatch<React.SetStateAction<any[]>>;
}

interface UseArticleSearchReturn {
  // State
  openSuggestArticles: boolean;
  articleSearchQuery: string;
  selectedArticles: any[];
  articlePreviewModal: ArticlePreviewModalState;
  isFetchingArticlePreview: boolean;

  // Setters
  setOpenSuggestArticles: React.Dispatch<React.SetStateAction<boolean>>;
  setArticleSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  setSelectedArticles: React.Dispatch<React.SetStateAction<any[]>>;

  // Handlers
  handleArticleSearchQuery: (query: string) => void;
  handleArticlePreview: (
    article: any,
    fromInlineSearch?: boolean
  ) => Promise<void>;
  handleCloseArticlePreview: () => void;
  handleBackToSearchFromPreview: (query: string) => void;
  handleSelectedArticlesChange: (articles: any[]) => void;
  handleSelectArticle: (article: any, type: "lead" | "embed") => void;
}

/**
 * Custom hook to manage article search and preview functionality
 * Extracted from MediaDetailsForm to improve organization and reusability
 */
export const useArticleSearch = ({
  formik,
  selectedArticleHost,
  createDenmarkEmbedSocialItem,
  currentMediaDetailsForEdit,
  localSocials,
  setLocalSocials,
}: UseArticleSearchProps): UseArticleSearchReturn => {
  const [openSuggestArticles, setOpenSuggestArticles] =
    useState<boolean>(false);
  const [articleSearchQuery, setArticleSearchQuery] = useState<string>("");
  const [selectedArticles, setSelectedArticles] = useState<any[]>([]);
  const isInitialLoad = useRef(true);

  const [articlePreviewModal, setArticlePreviewModal] =
    useState<ArticlePreviewModalState>({
      isOpen: false,
      article: null,
      articleDetails: null,
      loading: false,
      error: null,
      openedFromInlineSearch: false,
      inlineSearchQuery: "",
    });

  const [isFetchingArticlePreview, setIsFetchingArticlePreview] =
    useState<boolean>(false);

  const handleArticleSearchQuery = useCallback((query: string) => {
    setArticleSearchQuery(query);
  }, []);

  const handleCloseArticlePreview = useCallback(() => {
    setArticlePreviewModal({
      isOpen: false,
      article: null,
      articleDetails: null,
      loading: false,
      error: null,
      openedFromInlineSearch: false,
      inlineSearchQuery: "",
    });
  }, []);

  const handleArticlePreview = useCallback(
    async (article: any, fromInlineSearch = false) => {
      setIsFetchingArticlePreview(true);
      try {
        const response = await denMarkArticleGetApi(
          article.id,
          parseInt(selectedArticleHost, 10)
        );
        const responseTitle = (
          response?.data?.title ??
          response?.data?.name ??
          ""
        )
          .toString()
          .trim();
        if (response.status === 200 && response.data && responseTitle) {
          const updatedArticle = {
            ...article,
            isVideoEligible: response.data.isVideoEligible,
          };

          setArticlePreviewModal((prev) => ({
            ...prev,
            isOpen: true,
            article: updatedArticle,
            articleDetails: response.data,
            loading: false,
            error: null,
            openedFromInlineSearch: fromInlineSearch,
            inlineSearchQuery: fromInlineSearch ? articleSearchQuery : "",
          }));

          setSelectedArticles((prevArticles) =>
            prevArticles.map((a) =>
              a.id === article.id
                ? { ...a, isVideoEligible: response.data.isVideoEligible }
                : a
            )
          );
        } else {
          (toast as any).fail("Unable to fetch Article Details !");
          handleCloseArticlePreview();
        }
      } catch (error) {
        (toast as any).fail("Unable to fetch Article Details !");
        handleCloseArticlePreview();
      } finally {
        setIsFetchingArticlePreview(false);
      }
    },
    [selectedArticleHost, articleSearchQuery, handleCloseArticlePreview]
  );

  const handleBackToSearchFromPreview = useCallback((query: string) => {
    setArticlePreviewModal({
      isOpen: false,
      article: null,
      articleDetails: null,
      loading: false,
      error: null,
      openedFromInlineSearch: false,
      inlineSearchQuery: "",
    });
    setArticleSearchQuery(query);
    setTimeout(() => {
      setOpenSuggestArticles(true);
    }, 100);
  }, []);

  const handleSelectedArticlesChange = useCallback(
    (articles: any[]) => {
      const currentArticleIds = new Set(articles.map((a) => a.id));
      const removedArticleIds = selectedArticles
        .filter((a) => !currentArticleIds.has(a.id))
        .map((a) => a.id);
      const newSocialItems: any[] = [];

      articles.forEach((article) => {
        const existingSocialItem = localSocials.find(
          (social) =>
            social.id?.toString() === article.id ||
            social.section?.toString() === article.id
        );
        if (!existingSocialItem) {
          const uploadType = (article.selectedType ||
            article.selectedTypes?.[0] ||
            "lead") as "lead" | "embed";
          newSocialItems.push(
            createDenmarkEmbedSocialItem(article, uploadType)
          );
        }
      });

      if (newSocialItems.length > 0) {
        setLocalSocials((prev) => [...prev, ...newSocialItems]);
      }

      if (removedArticleIds.length > 0) {
        const currentDenmark = formik.values.denmark || [];
        const removedIdsSet = new Set(
          removedArticleIds.map((id) => id.toString())
        );
        const updatedDenmark = currentDenmark.filter(
          (d: any) =>
            !(
              d?.job_type === "denmark_embed" &&
              (removedIdsSet.has(d?.id?.toString()) ||
                removedIdsSet.has(d?.section?.toString()))
            )
        );
        if (updatedDenmark.length !== currentDenmark.length) {
          formik.setFieldValue("denmark", updatedDenmark, false);
        }
      }

      setSelectedArticles(articles);
    },
    [
      selectedArticles,
      localSocials,
      createDenmarkEmbedSocialItem,
      formik,
      setLocalSocials,
    ]
  );

  const handleSelectArticle = useCallback(
    (article: any, type: "lead" | "embed") => {
      const existingIndex = selectedArticles.findIndex(
        (a) => a.id === article.id
      );

      let updatedArticles;
      if (existingIndex !== -1) {
        const existingArticle = selectedArticles[existingIndex];
        const existingTypes =
          existingArticle.selectedTypes ||
          [existingArticle.selectedType].filter(
            (t): t is "lead" | "embed" => t === "lead" || t === "embed"
          );
        if (!existingTypes.includes(type)) {
          const newTypes = [...existingTypes, type];
          updatedArticles = [...selectedArticles];
          updatedArticles[existingIndex] = {
            ...existingArticle,
            selectedTypes: newTypes,
            selectedType: newTypes[newTypes.length - 1],
          };
        } else {
          updatedArticles = selectedArticles;
        }
      } else {
        const selectedArticle = {
          ...article,
          selectedTypes: [type],
          selectedType: type,
          selectedHost: selectedArticleHost,
        };
        updatedArticles = [...selectedArticles, selectedArticle];
        setLocalSocials((prev) => [
          ...prev,
          createDenmarkEmbedSocialItem(article, type),
        ]);
      }
      setSelectedArticles(updatedArticles);
    },
    [
      selectedArticles,
      selectedArticleHost,
      createDenmarkEmbedSocialItem,
      setLocalSocials,
    ]
  );

  // Initialize selectedArticles from currentMediaDetailsForEdit.socials
  useEffect(() => {
    if (currentMediaDetailsForEdit?.socials) {
      const validArticleEmbeds = currentMediaDetailsForEdit.socials
        .filter(
          (item: any) =>
            item.job_type === "denmark_embed" &&
            (item.upload_type === "lead" || item.upload_type === "embed") &&
            item.id &&
            (item.channel_name || item.title) &&
            item.channel_name !== "" &&
            item.title !== ""
        )
        .map((item: any) => ({
          id: item.id?.toString(),
          title: item.title,
          description: item.desc,
          msid: item.id?.toString(),
          selectedType: item.upload_type === "embed" ? "embed" : "lead",
          selectedTypes: [item.upload_type === "embed" ? "embed" : "lead"],
          channel_name: item.channel_name,
          status: item.status,
          mt: item.mt,
          ct: item.ct,
        }));

      setSelectedArticles((prevArticles: any[]) => {
        const reduxIds = new Set(validArticleEmbeds.map((a: any) => a.id));
        const temporaryArticles = prevArticles.filter(
          (prev: any) =>
            !reduxIds.has(prev.id) && (prev.status === 100 || !prev.status)
        );
        return [...validArticleEmbeds, ...temporaryArticles];
      });

      if (isInitialLoad.current) {
        isInitialLoad.current = false;
      }
    }
  }, [currentMediaDetailsForEdit?.socials]);

  return {
    // State
    openSuggestArticles,
    articleSearchQuery,
    selectedArticles,
    articlePreviewModal,
    isFetchingArticlePreview,

    // Setters
    setOpenSuggestArticles,
    setArticleSearchQuery,
    setSelectedArticles,

    // Handlers
    handleArticleSearchQuery,
    handleArticlePreview,
    handleCloseArticlePreview,
    handleBackToSearchFromPreview,
    handleSelectedArticlesChange,
    handleSelectArticle,
  };
};
